import {
  useAddFavoritesProductMutation,
  useDeleteFavoritesProductMutation,
} from '@graphql/mutations/__generated__/me.generated'
import { useMeQuery } from '@graphql/queries/__generated__/me.generated'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import Loader from '@components/Loader'
import FavoritesBtn from './FavoritesBtn'
import { useTranslation } from 'react-i18next'
import { DataLayerItem, googlePushProductAddToWishlist } from '../../../utils/dataLayer'
import { DEFAULT_CURRENCY } from '../../../../constants'

interface IProps {
  pk: number
  isInFavorites?: boolean
  addToFavoritesSuccess?(pk: number): void
  deleteFromFavoritesSuccess?(pk: number): void
}

const FavoritesBtnView: React.FC<IProps> = ({
  pk,
  isInFavorites,
  addToFavoritesSuccess,
  deleteFromFavoritesSuccess,
}) => {
  const { t } = useTranslation()
  const userQuery = useMeQuery()
  const { enqueueSnackbar } = useSnackbar()
  const [loading, setLoading] = useState(false)
  const [addProductToFavorites] = useAddFavoritesProductMutation({
    refetchQueries: ['MeFavoritesProducts', 'MeFavoritesReflections', 'MeFavoritesCategoriesInfo'],
    awaitRefetchQueries: true,
  })
  const [deleteProductFromFavorites] = useDeleteFavoritesProductMutation({
    refetchQueries: ['MeFavoritesProducts', 'MeFavoritesReflections', 'MeFavoritesCategoriesInfo'],
    awaitRefetchQueries: false,
  })

  const onAddToFavorites = async () => {
    setLoading(true)
    try {
      const { data } = await addProductToFavorites({
        variables: { pk },
      })
      const product = data.userAddFavoritesProduct.product
      const items: DataLayerItem[] = [
        {
          item_id: product.sku,
          item_name: product.name,
          index: 0,
          quantity: 1,
        },
      ]
      googlePushProductAddToWishlist({ currency: DEFAULT_CURRENCY, value: 0, items })
      if (addToFavoritesSuccess) addToFavoritesSuccess(pk)
      enqueueSnackbar(t('snackbarMessages.success.productAddedToFavorites', 'Product has been added to favorites.'), {
        variant: 'success',
      })
    } catch (e) {
      enqueueSnackbar(e.message, {
        variant: 'error',
      })
    }
    setLoading(false)
  }
  const onDeleteFromFavorites = async () => {
    setLoading(true)
    try {
      await deleteProductFromFavorites({
        variables: { pk },
      })
      if (deleteFromFavoritesSuccess) deleteFromFavoritesSuccess(pk)
      enqueueSnackbar(
        t('snackbarMessages.error.productRemovedFromFavorites', 'Product has been deleted from favorites.'),
        {
          variant: 'error',
        }
      )
    } catch (e) {
      enqueueSnackbar(e.message, {
        variant: 'error',
      })
    }
    setLoading(false)
  }

  return (
    <>
      <Loader className='spinner global' type='ThreeDots' color='#333' visible={loading} height={100} width={100} />
      <FavoritesBtn
        isInFavorites={isInFavorites}
        isAuthenticated={!!userQuery.data?.me}
        onAddToFavorites={onAddToFavorites}
        onDeleteFromFavorites={onDeleteFromFavorites}
      />
    </>
  )
}

export default FavoritesBtnView
